import type {
  ReactNode,
} from 'react';
import React, {
  createContext,
  useCallback,
  useContext,
  useState,
} from 'react';

export type MessageType = 'success' | 'error' | 'warning' | 'info';
export type ModalType = 'message' | 'action';

export interface ModalConfig {
  type?: MessageType;
  modalType?: ModalType;
  header?: string;
  message?: string | ReactNode | Element;
  size?: 'small' | 'medium' | 'large' | 'xlarge';
  maxWidth?: string | number; // Custom max width (overrides size if provided)
  fullScreen?: boolean; // Makes the modal use the full viewport
  confirmLabel?: string;
  cancelLabel?: string;
  onConfirm?: () => boolean | Promise<boolean | void> | void;
  onCancel?: () => void;
  duration?: number;
  customBody?: React.ReactNode;
  confirmButtonColor?: 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success';
  disableEscapeKeyDown?: boolean;
}

export interface MessageContextValue {
  modalConfig: ModalConfig | null;
  openMessage: (config: ModalConfig) => void;
  closeMessage: () => void;
}

const MessageServiceContext = createContext<MessageContextValue | undefined>(
  undefined,
);

export const MessageServiceProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const [modalConfig, setModalConfig] = useState<ModalConfig | null>(null);

  const openMessage = useCallback((config: ModalConfig) => {
    setModalConfig({
      modalType: 'message',
      ...config,
    });

    if (config.duration) {
      setTimeout(() => {
        setModalConfig(null);
      }, config.duration);
    }
  }, []);

  const closeMessage = useCallback(() => {
    setModalConfig(null);
  }, []);

  return (
    <MessageServiceContext.Provider
      value={{
        modalConfig,
        openMessage,
        closeMessage,
      }}
    >
      {children}
    </MessageServiceContext.Provider>
  );
};

export function useMessageService() {
  const context = useContext(MessageServiceContext);
  if (!context) {
    throw new Error(
      'useMessageService must be used within a MessageServiceProvider',
    );
  }
  return context;
}
